import {
  SetStateAction,
  Dispatch,
  ChangeEvent,
  useEffect,
  useState,
} from 'react';
import {
  DevHubFormControl,
  DevHubFormControlLabel,
  DevHubFormLabel,
  DevHubGrid,
  DevHubRadio,
  DevHubRadioGroup,
} from '@devxdiscover/devhub-ui';
import { privacySurvey } from './privacySurvey';

interface Props {
  privacyData: any;
  setPrivacyData: Dispatch<SetStateAction<any>>;
  setValidationHasError: Dispatch<SetStateAction<boolean>>;
}

/**
 * Component to gather application privacy details
 * @param props - The properties passed to the component.
 * @public
 */
export const AppCreationPrivacyDetails = (props: Props) => {
  const { privacyData, setPrivacyData } = props;
  const [pii, setPii] = useState<boolean>();
  const [piPersist, setPiPersist] = useState<boolean>();

  const validateKeys = (): boolean => {
    let hasError = false;
    const expectedObjectKeys = [
      'CPP_CERT2_7',
      'CPP_CERT2_8',
      'CPP_CERT2_9',
      'CPP_CERT2_10',
      'CPP_CERT2_11',
      'CPP_CERT2_12',
      'CPP_CERT2_13',
    ];

    expectedObjectKeys.forEach(item => {
      if (!privacyData.hasOwnProperty(item)) {
        hasError = true;
      } else {
        if (privacyData?.[item] === undefined) {
          hasError = true;
        }
      }
    });
    return hasError;
  };

  const validateForm = (): boolean => {
    let hasError = true;

    if (!pii && pii !== undefined) hasError = false;

    if (pii && !piPersist && piPersist !== undefined) hasError = false;

    if (pii && piPersist) {
      hasError = validateKeys();
    }
    return hasError;
  };
  props.setValidationHasError(validateForm());

  useEffect(() => {
    // TODO:
    setPii(privacyData?.CPP_CERT2_7);
    setPiPersist(privacyData?.CPP_CERT2_8);
  }, [pii, piPersist, privacyData]);

  const getBoolean = (value: string): boolean => {
    return value === 'yes';
  };

  const boolToString = (value: boolean | undefined): string | undefined => {
    if (value === undefined) return undefined;
    return value ? 'yes' : 'no';
  };

  const onFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    const key = event?.target?.name;
    const newValue = getBoolean(event?.target?.value);
    let prevDetails = privacyData;

    if (key === 'CPP_CERT2_7') {
      prevDetails = {};
      setPii(newValue);
    }

    if (key === 'CPP_CERT2_8') {
      const piiSelection = prevDetails?.CPP_CERT2_7;
      prevDetails = {};
      prevDetails.CPP_CERT2_7 = piiSelection;
      setPiPersist(newValue);
    }

    prevDetails[key] = newValue;

    setPrivacyData(prevDetails);
    props.setValidationHasError(validateForm());
  };

  return (
    <DevHubGrid container spacing={2}>
      <DevHubGrid item xs={12}>
        <DevHubGrid alignItems="flex-start">
          <DevHubFormControl margin="normal">
            <DevHubFormLabel>
              * {privacySurvey?.CPP_CERT2_7?.text}
            </DevHubFormLabel>
            <DevHubRadioGroup
              onChange={onFormChange}
              defaultValue={boolToString(privacyData?.CPP_CERT2_7)}
            >
              <DevHubFormControlLabel
                value="yes"
                control={<DevHubRadio />}
                name="CPP_CERT2_7"
                label="Yes"
              />
              <DevHubFormControlLabel
                value="no"
                control={<DevHubRadio />}
                name="CPP_CERT2_7"
                label="No"
              />
            </DevHubRadioGroup>
          </DevHubFormControl>
          {pii && (
            <>
              <DevHubFormControl margin="normal">
                <DevHubFormLabel>
                  * {privacySurvey?.CPP_CERT2_8?.text}
                </DevHubFormLabel>
                <DevHubRadioGroup
                  onChange={onFormChange}
                  defaultValue={boolToString(privacyData?.CPP_CERT2_8)}
                >
                  <DevHubFormControlLabel
                    value="yes"
                    control={<DevHubRadio />}
                    name="CPP_CERT2_8"
                    label="Yes"
                  />
                  <DevHubFormControlLabel
                    value="no"
                    control={<DevHubRadio />}
                    name="CPP_CERT2_8"
                    label="No"
                  />
                </DevHubRadioGroup>
              </DevHubFormControl>
              {piPersist && (
                <>
                  <div>
                    <DevHubFormControl margin="normal">
                      <DevHubFormLabel>
                        * {privacySurvey?.CPP_CERT2_9?.text}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        name="CPP_CERT2_9"
                        onChange={onFormChange}
                        defaultValue={boolToString(privacyData.CPP_CERT2_9)}
                      >
                        <DevHubFormControlLabel
                          value="yes"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_9"
                          label="Yes"
                        />
                        <DevHubFormControlLabel
                          value="no"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_9"
                          label="No"
                        />
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </div>
                  <div>
                    <DevHubFormControl margin="normal">
                      <DevHubFormLabel>
                        * {privacySurvey?.CPP_CERT2_10?.text}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        onChange={onFormChange}
                        name="pii"
                        defaultValue={boolToString(privacyData.CPP_CERT2_10)}
                      >
                        <DevHubFormControlLabel
                          value="yes"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_10"
                          label="Yes"
                        />
                        <DevHubFormControlLabel
                          value="no"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_10"
                          label="No"
                        />
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </div>
                  <div>
                    <DevHubFormControl margin="normal">
                      <DevHubFormLabel>
                        * {privacySurvey?.CPP_CERT2_11?.text}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        onChange={onFormChange}
                        defaultValue={boolToString(privacyData.CPP_CERT2_11)}
                      >
                        <DevHubFormControlLabel
                          value="yes"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_11"
                          label="Yes"
                        />
                        <DevHubFormControlLabel
                          value="no"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_11"
                          label="No"
                        />
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </div>
                  <div>
                    <DevHubFormControl margin="normal">
                      <DevHubFormLabel>
                        * {privacySurvey?.CPP_CERT2_12?.text}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        onChange={onFormChange}
                        defaultValue={boolToString(privacyData.CPP_CERT2_12)}
                      >
                        <DevHubFormControlLabel
                          value="yes"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_12"
                          label="Yes"
                        />
                        <DevHubFormControlLabel
                          value="no"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_12"
                          label="No"
                        />
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </div>
                  <div>
                    <DevHubFormControl margin="normal">
                      <DevHubFormLabel>
                        * {privacySurvey?.CPP_CERT2_13?.text}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        onChange={onFormChange}
                        defaultValue={boolToString(privacyData.CPP_CERT2_13)}
                      >
                        <DevHubFormControlLabel
                          value="yes"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_13"
                          label="Yes"
                        />
                        <DevHubFormControlLabel
                          value="no"
                          control={<DevHubRadio />}
                          name="CPP_CERT2_13"
                          label="No"
                        />
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </div>
                </>
              )}
            </>
          )}
        </DevHubGrid>
      </DevHubGrid>
    </DevHubGrid>
  );
};
